import { useRef } from 'react'
import type { MutableRefObject, ReactNode } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import {
  ANTRACIET, Bouten, Camera3D, Carrosserie, Markeringslamp, Merkplaat, Scanner, Sensorkop,
  Vork, Waarschuwingsplaat, Wiel, buisGeometrie, kapGeometrie,
} from './onderdelen'
import type { TruckKleuren } from './onderdelen'

/**
 * T-Matic — autonome pallettruck voor horizontaal transport.
 *
 * ## Waar de maten vandaan komen
 *
 * Niet uit het oog maar uit de fabrieksfoto's. Die zijn RGBA met een echt
 * alfakanaal, dus het silhouet is exact uit te lezen. Uit het zijaanzicht:
 * het omhullende kader is 468 × 468 pixels — de machine is precies even hoog
 * als lang. De vorken beslaan 42–100 % van de lengte. Eén harde ankerwaarde
 * zet dat om naar meters: een vork moet 1150 mm zijn, anders past er geen
 * europallet op. Daaruit volgt de rest:
 *
 * ```
 * lengte 1,98 m   hoogte 1,98 m   breedte 0,80 m
 * ```
 *
 * De opbouw over de lengte (0 = achter, 1 = vorkpunt), afgelezen uit het
 * bovenprofiel van hetzelfde silhouet:
 *
 * | zone            | lengte      | hoogte    |
 * |-----------------|-------------|-----------|
 * | rode rok        | 0,00 – 0,42 | tot 0,16  |
 * | zwarte motorkap | 0,02 – 0,19 | tot 0,61  |
 * | rood accublok   | 0,19 – 0,41 | tot 0,44  |
 * | zwart deksel    | 0,26 – 0,41 | tot 0,52  |
 * | A-frame         | 0,13 – 0,19 | tot 0,95  |
 * | sensorbalk      | 0,115– 0,25 | tot 1,00  |
 * | vorken          | 0,42 – 1,00 | tot 0,08  |
 *
 * Breedtes komen uit het achteraanzicht (208 × 509 px): rok 0,80 m,
 * accublok 0,71 m, motorkap 0,69 m, sensorbalk 0,67 m, A-frame 0,41 m boven
 * en 0,57 m onder. De navigatiekop is 0,139 × de breedte = 111 mm.
 *
 * Kleuren zijn uit de vlakke, belichte vlakken bemonsterd: rood [185,35,44],
 * kunststof [78,78,78] — de "zwarte" delen zijn antraciet, geen zwart.
 *
 * ## En waar de harde maten vandaan komen
 *
 * Uit de VDI-maattekening: aandrijfwiel Ø 254, lastrollen Ø 85 op 89 mm
 * hartafstand, achteroverbouw 335 mm vanaf het hart van het aandrijfwiel, en
 * 218 mm hoogte achteronder waar de afloophoek van 8° begint. Die vijf
 * vervangen een schatting door een typewaarde.
 *
 * Voorbehoud: die tekening is de eerste generatie, de T-MATIC 131-01, met
 * dissel, een gebogen sensorframe en vorken van 2400 mm. Dit model is de
 * nieuwere generatie met het A-frame. Chassis en wielen zijn gedeeld en
 * overgenomen; de bovenbouw niet — de 2370 mm daar is de bovenkant van het
 * oude sensorframe. Zie docs/tmatic-maten.md.
 *
 * ## Wat hem T-Matic maakt
 *
 * Géén hefmast. De vorken liggen op 85 mm en heffen 125 mm. Wat omhoog steekt
 * is een gebogen A-frame van twee buizen met een sensorbalk erbovenop: twee
 * schuine scanners aan de uiteinden, een stereocamera in het midden en de
 * navigatielaser erbovenop.
 */

/** De twee gebogen A-framebuizen. Eén keer gebouwd, gedeeld door alle T-Matics. */
const BEEN = ([-1, 1] as const).map((s) =>
  buisGeometrie(
    [
      [s * 0.300, 0.62, -0.740],
      [s * 0.296, 0.90, -0.722],
      [s * 0.258, 1.10, -0.708],
      [s * 0.238, 1.33, -0.692],
      [s * 0.219, 1.45, -0.681],
      [s * 0.190, 1.60, -0.668],
      [s * 0.158, 1.78, -0.648],
      [s * 0.143, 1.90, -0.634],
    ],
    0.029,
  ),
)

/**
 * De motorkap als één getrokken vlak: onder 0,665 m breed en 0,35 m diep, boven
 * 0,57 × 0,335. Gestapelde dozen geven hier traptreden, en dat is precies het
 * detail waaraan een model als model leest.
 */
const KAP = kapGeometrie(0.665, 0.77, 0.35, 0.045, 0.857, 0.957)

export function TMatic({ kleuren, hef, stand, lading }: {
  kleuren: TruckKleuren
  hef: MutableRefObject<number>
  /**
   * Vorkhoogte boven de laagste stand, in meters, per frame. Zonder dit volgt
   * hij `hef` (0..1 van h3) — zo gebruiken de showroom en de vergelijking hem.
   */
  stand?: MutableRefObject<{ hoogte: number }>
  /** Wat er op de vorken ligt, in het stelsel van de vorken (`heenEnWeer.ts`). */
  lading?: ReactNode
}) {
  const vorken = useRef<THREE.Group>(null)

  useFrame(() => {
    // Hefhoogte van een pallettruck: h3 = 120 mm (typeblad), niet meer.
    if (vorken.current) vorken.current.position.y = stand ? stand.current.hoogte : hef.current * 0.12
  })

  return (
    <group>
      {/* ================================================== onderstel / rode rok
          Breedste punt van de hele machine (0,80 m) op 0,20 m hoogte. */}
      <Carrosserie args={[0.815, 0.26, 0.79]} positie={[0, 0.205, -0.555]} kleur={kleuren.rood}
        straal={0.032} ruwheid={0.36} metaal={0.05} lak />
      {/* Onderrand die naar binnen loopt — op de foto's trekt de rok onderaan
          smaller. Korter dan de rok erboven, want achterin loopt hij omhoog weg. */}
      <Carrosserie args={[0.665, 0.1, 0.60]} positie={[0, 0.05, -0.475]} kleur={kleuren.rood}
        straal={0.03} ruwheid={0.42} metaal={0.05} lak />
      {/* De maattekening geeft achteronder 218 mm op een afloophoek van 8°. Dat
          zit aan de onderkant van het chassis en is vanuit geen enkele
          kioskcamera te zien; als volume gemodelleerd duwde het juist het
          silhouet uit model. Vandaar alleen de kortere onderrand hierboven. */}
      {/* Achterlip: op het silhouet steekt er achter nog een randje uit op 0,10–0,13 h */}
      <mesh position={[0, 0.225, -0.962]} castShadow>
        <boxGeometry args={[0.76, 0.1, 0.05]} />
        <meshStandardMaterial color={kleuren.rood} roughness={0.38} metalness={0.03} />
      </mesh>
      {/* Zwarte naad tussen rok en accublok */}
      <mesh position={[0, 0.325, -0.4]}>
        <boxGeometry args={[0.745, 0.02, 0.62]} />
        <meshStandardMaterial color="#1e1f23" roughness={0.75} metalness={0.15} />
      </mesh>
      {/* Bouten in de rok */}
      <Bouten normaal="z" posities={[
        [-0.3, 0.225, -0.984], [-0.12, 0.225, -0.984], [0.12, 0.225, -0.984], [0.3, 0.225, -0.984],
        [-0.3, 0.31, -0.953], [0.3, 0.31, -0.953], [-0.3, 0.12, -0.953], [0.3, 0.12, -0.953],
      ]} />
      {/* Zwarte koppeling midden achterin */}
      <mesh position={[0, 0.2, -0.975]} castShadow>
        <boxGeometry args={[0.075, 0.17, 0.05]} />
        <meshStandardMaterial color="#232428" roughness={0.5} metalness={0.45} />
      </mesh>
      {/* Gele waarschuwingsplaatjes onderaan, links en rechts */}
      <Waarschuwingsplaat positie={[-0.36, 0.13, -0.93]} rotatie={[0, -Math.PI / 2, 0]} />
      <Waarschuwingsplaat positie={[0.36, 0.13, -0.93]} rotatie={[0, Math.PI / 2, 0]} />

      {/* ================================================== zwarte motorkap
          Smalle, hoge toren boven de aandrijving: 0,33 m diep, onder 0,70 m breed
          en naar boven toe smaller, tot 1,05 m. Die versmalling is wat hem op het
          achteraanzicht van een doos onderscheidt — hij loopt van 0,88 naar 0,71
          van de machinebreedte. Daarop kantelt het bedieningspaneel naar achter. */}
      <mesh geometry={KAP} position={[0, 0.70, -0.788]} castShadow receiveShadow>
        <meshPhysicalMaterial
          color={ANTRACIET} roughness={0.55} metalness={0.06}
          clearcoat={0.2} clearcoatRoughness={0.4}
        />
      </mesh>
      {/* Greepschouders links en rechts, op 0,94–1,07 m — op het achteraanzicht
          is dat de breedste plek van de kap. */}
      {([-1, 1] as const).map((s) => (
        <Carrosserie key={s} args={[0.07, 0.13, 0.22]} positie={[s * 0.30, 1.0, -0.80]}
          kleur={ANTRACIET} straal={0.028} ruwheid={0.55} metaal={0.16} />
      ))}
      {/* Rode flanken van de aandrijfunit: op het zijaanzicht loopt het rood van
          0,33 naar 0,41 hoogte op langs de kap. */}
      {([-1, 1] as const).map((s) => (
        <mesh key={s} position={[s * 0.35, 0.58, -0.72]} castShadow>
          <boxGeometry args={[0.024, 0.5, 0.24]} />
          <meshStandardMaterial color={kleuren.rood} roughness={0.34} metalness={0.03} />
        </mesh>
      ))}
      {/* Zijpaneel met twee ronde knoppen, zoals op de foto's */}
      {([-1, 1] as const).map((s) => (
        <group key={s} position={[s * 0.334, 0.86, -0.72]} rotation={[0, s * Math.PI / 2, 0]}>
          <mesh>
            <boxGeometry args={[0.16, 0.13, 0.006]} />
            <meshStandardMaterial color="#303136" roughness={0.5} metalness={0.2} />
          </mesh>
          {[-0.032, 0.032].map((x) => (
            <mesh key={x} position={[x, 0, 0.006]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.017, 0.017, 0.008, 12]} />
              <meshStandardMaterial color="#8d939b" roughness={0.35} metalness={0.6} />
            </mesh>
          ))}
        </group>
      ))}
      {/* Linde-embleem op de achterkant van de kap — het enige wat dat vlak breekt */}
      <Merkplaat positie={[0, 0.82, -0.962]} rotatie={[0, Math.PI, 0]} breedte={0.40} />

      {/* ---- bedieningspaneel: kantelt naar de bedienzijde (achter) ---- */}
      <group position={[0, 1.115, -0.80]} rotation={[-0.34, 0, 0]}>
        <Carrosserie args={[0.55, 0.095, 0.30]} positie={[0, 0, 0]} kleur={ANTRACIET}
          straal={0.03} ruwheid={0.5} metaal={0.18} />
        {/* Verzonken lichtgrijs paneel */}
        <mesh position={[0, 0.042, 0.005]}>
          <boxGeometry args={[0.38, 0.012, 0.17]} />
          <meshStandardMaterial color="#9096a0" roughness={0.5} metalness={0.12} />
        </mesh>
        {/* Bedienknoppen: een rij donkere kappen met één rode noodstop */}
        {[-0.12, -0.05, 0.02, 0.09].map((x) => (
          <mesh key={x} position={[x, 0.056, -0.025]}>
            <cylinderGeometry args={[0.016, 0.018, 0.026, 10]} />
            <meshStandardMaterial color="#1c1d21" roughness={0.4} metalness={0.3} />
          </mesh>
        ))}
        <mesh position={[0.15, 0.058, -0.025]}>
          <cylinderGeometry args={[0.022, 0.024, 0.03, 12]} />
          <meshStandardMaterial color="#c8202a" roughness={0.35} metalness={0.1} />
        </mesh>
        {/* Displayvlakje */}
        <mesh position={[-0.05, 0.055, 0.06]} rotation={[-0.1, 0, 0]}>
          <boxGeometry args={[0.15, 0.004, 0.07]} />
          <meshStandardMaterial
            color="#16222e" emissive="#1d3550" emissiveIntensity={0.35} roughness={0.2}
          />
        </mesh>
      </group>

      {/* ================================================== rood accublok
          0,44 m diep, 0,71 m breed, van de rok tot 0,87 m. Eén schone massa —
          op de foto's zit de vorm in de afronding en de naadlijn, niet in trappen. */}
      <Carrosserie args={[0.71, 0.63, 0.41]} positie={[0, 0.61, -0.415]} kleur={kleuren.rood}
        straal={0.024} ruwheid={0.34} metaal={0.05} lak />
      {/* Afgezette voorkant: op het silhouet zakt het rood daar van 0,47 naar 0,36. */}
      <Carrosserie args={[0.712, 0.42, 0.055]} positie={[0, 0.5, -0.192]} kleur={kleuren.rood}
        straal={0.02} ruwheid={0.34} metaal={0.05} lak />
      {/* Naadlijn langs de achterrand van het blok */}
      {([-1, 1] as const).map((s) => (
        <mesh key={s} position={[s * 0.3565, 0.62, -0.555]}>
          <boxGeometry args={[0.004, 0.56, 0.02]} />
          <meshStandardMaterial color="#8c1a20" roughness={0.5} metalness={0.35} />
        </mesh>
      ))}
      {/* Bouten op de flanken */}
      {([-1, 1] as const).map((s) => (
        <Bouten key={s} normaal="x" posities={[
          [s * 0.357, 0.84, -0.52], [s * 0.357, 0.44, -0.52], [s * 0.357, 0.64, -0.25],
        ]} />
      ))}
      {/* Zwart deksel op het accublok: plat, met een verzonken goot erin, en het
          loopt vóór het blok af — op het silhouet zakt de bovenkant daar van
          0,52 naar 0,36 hoogte. */}
      <Carrosserie args={[0.665, 0.11, 0.40]} positie={[0, 0.975, -0.425]} kleur="#35363b"
        straal={0.018} ruwheid={0.55} metaal={0.08} />
      <mesh position={[0, 1.021, -0.425]}>
        <boxGeometry args={[0.42, 0.016, 0.22]} />
        <meshStandardMaterial color="#232429" roughness={0.62} metalness={0.15} />
      </mesh>
      {/* Voorkant van het blok: rond luikje en een plaatje, zoals op het vooraanzicht */}
      <mesh position={[0, 0.62, -0.157]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.022, 0.022, 0.006, 12]} />
        <meshStandardMaterial color="#2c2e33" roughness={0.45} metalness={0.6} />
      </mesh>
      <Waarschuwingsplaat positie={[0, 0.4, -0.157]} />

      {/* ================================================== vorken
          Beginnen precies waar het accublok eindigt (f 0,42) en lopen tot de
          punt (f 1,00): 1,15 m, de standaardlengte voor een europallet. */}
      <group ref={vorken}>
        {/* Vorkenframe, blijft achter de rok — op de foto's zie je het niet. */}
        <mesh position={[0, 0.13, -0.22]} castShadow>
          <boxGeometry args={[0.5, 0.15, 0.08]} />
          <meshStandardMaterial color={kleuren.rood} roughness={0.42} metalness={0.03} />
        </mesh>
        <group position={[0, 0, -0.16]}>
          <Vork x={-0.195} lengte={1.15} />
          <Vork x={0.195} lengte={1.15} />
        </group>

        {lading}
      </group>

      {/* ================================================== wielen
          Aandrijfwiel Ø 254 mm met zijn hart 335 mm vóór de achterkant, allebei
          van de maattekening. Twee zwenkwielen in de hoeken van de rok, precies
          waar ze op het achteraanzicht onderuit steken. */}
      <Wiel positie={[0, 0.127, -0.655]} straal={0.127} breedte={0.11} />
      <Wiel positie={[-0.295, 0.06, -0.9]} straal={0.06} breedte={0.05} />
      <Wiel positie={[0.295, 0.06, -0.9]} straal={0.06} breedte={0.05} />

      {/* ================================================== A-frame
          Twee gebogen buizen, Ø 58 mm. Ze staan onder wijd en lopen naar boven
          naar elkaar toe, met een lichte voorwaartse helling van zo'n 6°. */}
      {BEEN.map((geom, i) => (
        <mesh key={i} geometry={geom} castShadow>
          <meshStandardMaterial color={ANTRACIET} roughness={0.42} metalness={0.08} />
        </mesh>
      ))}
      {/* Gietijzeren voetstuk waar de buis de kap in gaat */}
      {([-1, 1] as const).map((s) => (
        <Carrosserie key={s} args={[0.078, 0.22, 0.105]} positie={[s * 0.30, 0.70, -0.74]}
          kleur={ANTRACIET} straal={0.025} ruwheid={0.5} metaal={0.35} />
      ))}
      {/* Dwarsverbinding vlak onder de balk */}
      <mesh position={[0, 1.845, -0.641]} castShadow>
        <boxGeometry args={[0.30, 0.035, 0.045]} />
        <meshStandardMaterial color={ANTRACIET} roughness={0.45} metalness={0.4} />
      </mesh>
      {/* Markeringslampen op de buitenkant van de buizen, op 1,31 m */}
      <Markeringslamp positie={[-0.256, 1.31, -0.693]} rotatie={[0, 0, Math.PI]} />
      <Markeringslamp positie={[0.256, 1.31, -0.693]} />
      {/* Rode noodstop op de voorkant van de rechterbuis */}
      <mesh position={[0.218, 1.46, -0.652]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.021, 0.021, 0.03, 12]} />
        <meshStandardMaterial color="#c8202a" roughness={0.35} />
      </mesh>

      {/* ================================================== sensorbalk
          0,67 m breed, 0,27 m diep, onderkant op 1,87 m. Daaronder twee schuine
          scanners en een stereocamera; erbovenop de draaiende navigatielaser. */}
      <group position={[0, 1.912, -0.632]}>
        <Carrosserie args={[0.67, 0.058, 0.27]} positie={[0, 0, 0]} kleur={ANTRACIET}
          straal={0.026} ruwheid={0.44} metaal={0.3} />
        {/* Lichtere band langs de onderrand — die vangt op de foto's het licht */}
        <mesh position={[0, -0.028, 0]}>
          <boxGeometry args={[0.652, 0.016, 0.252]} />
          <meshStandardMaterial color="#6a6d74" roughness={0.38} metalness={0.45} />
        </mesh>
        {/* Twee scanners aan de uiteinden, schuin naar buiten en omlaag. Ze hangen
            een stuk onder de balk uit: op het achteraanzicht zitten ze op 0,87–0,94
            hoogte terwijl de balk op 0,945 begint. */}
        {([-1, 1] as const).map((s) => (
          <group key={s} position={[s * 0.25, -0.108, 0.045]} rotation={[-0.34, s * 0.5, 0]}>
            <Scanner schaal={1.5} />
          </group>
        ))}
        {/* Vlakke module in het midden, tegen de balk aan */}
        <mesh position={[0, -0.048, 0.06]}>
          <boxGeometry args={[0.2, 0.038, 0.09]} />
          <meshStandardMaterial color="#2a2b2f" roughness={0.45} metalness={0.35} />
        </mesh>
        {/* Stereocamera aan een beugeltje eronder */}
        <mesh position={[0, -0.09, 0.055]}>
          <boxGeometry args={[0.04, 0.05, 0.02]} />
          <meshStandardMaterial color="#26272b" roughness={0.5} metalness={0.4} />
        </mesh>
        <group position={[0, -0.135, 0.07]} rotation={[-0.45, 0, 0]}>
          <Camera3D />
        </group>
        {/* Navigatielaser bovenop, iets achter het midden. Hij steekt maar
            30 mm boven de balk uit — hoger en het wordt een paddenstoel. */}
        <group position={[0, 0.022, -0.012]}>
          <Sensorkop />
        </group>
      </group>

      {/* ================================================== veiligheidsscanner vooraan
          Kijkt laag over de vloer, tussen de vorken door. */}
      <group position={[0, 0.235, -0.15]} rotation={[0.1, 0, 0]}>
        <Carrosserie args={[0.15, 0.11, 0.09]} positie={[0, 0, 0]} kleur="#2a2b2f"
          straal={0.02} ruwheid={0.44} metaal={0.4} />
        <mesh position={[0, 0, 0.048]}>
          <boxGeometry args={[0.11, 0.07, 0.005]} />
          <meshStandardMaterial color="#0b1016" roughness={0.12} metalness={0.75} />
        </mesh>
      </group>
    </group>
  )
}
